/**
 * Almacenamiento a Domicilio — pillar-landing-page surface helpers.
 *
 * Centralizes the pillar+coverage gating logic shared between the FaqsSection,
 * ContentHub on-demand tile, and the page-level FAQPageJsonLd schema in
 * `pages/[pillar]/index.tsx`. Keeping a single resolver here ensures the
 * rendered FAQ entry, the rendered ContentHub tile, and the JSON-LD schema all
 * agree on visibility and destination URL — drift between them would either
 * leak the schema entry on uncovered pages (SEO penalty) or hide a rendered
 * answer from Google's FAQ rich-result eligibility.
 *
 * Mirrors the `resolveCrossLinkOnDemand` helper in
 * `src/components/PersonalizedLanding/CrossLinkOnDemand.tsx` — same pillar
 * gate, same coverage gate, same URL resolution.
 */

import { PillarPage } from 'src/constants/searchPages'
import { hasAnyOnDemandCoverage, resolveOnDemandCity } from 'src/utils/onDemand'

/**
 * Resolves the destination URL for the on-demand FAQ entry / ContentHub tile.
 * Returns `null` when the surface should not render at all (wrong pillar,
 * unresolved/inactive city, or pillar-root with zero active coverage).
 *
 * - Pillar root (no `currentCity`): generic parent URL when at least one
 *   on-demand market has `partnerActive: true`. Returns `null` otherwise.
 * - City / hood pages: city-aware URL when the resolved market has
 *   `partnerActive: true`. Returns `null` otherwise.
 * - Non-SelfStorage pillars: always returns `null`.
 */
export function resolveOnDemandLandingUrl(
  pillar: PillarPage,
  currentCity: string | undefined
): string | null {
  if (pillar !== PillarPage.SelfStorage) return null

  const hasCity = typeof currentCity === 'string' && currentCity.length > 0

  if (hasCity) {
    const resolved = resolveOnDemandCity(currentCity)
    if (!resolved || !resolved.partnerActive) return null
    return `/${PillarPage.SelfStorage}/almacenamiento-a-domicilio/${resolved.config.canonicalSlug}`
  }

  if (!hasAnyOnDemandCoverage()) return null
  return `/${PillarPage.SelfStorage}/almacenamiento-a-domicilio`
}

/** Splits a single anchor-tagged HTML segment ("…<a href=\"X\">label</a>…")
 *  into a structured triplet so React can render the link without
 *  `dangerouslySetInnerHTML`. The locale copy is authored with exactly one
 *  inline `<a>` tag — additional tags are ignored (only the first match is
 *  honored) and unmatched input falls back to `{ before: input }` so the FAQ
 *  body is never blank. */
export function parseSingleAnchor(input: string): {
  before: string
  href?: string
  label?: string
  after?: string
} {
  // Non-greedy match for one anchor tag. Locale-authored only; the regex is
  // intentionally narrow because we're not parsing arbitrary HTML.
  const match = input.match(
    /^([\s\S]*?)<a\s+href="([^"]+)">([\s\S]*?)<\/a>([\s\S]*)$/
  )
  if (!match) return { before: input }
  const [, before, href, label, after] = match
  return { before, href, label, after }
}

/** A FAQ entry suitable for both the page-level `FAQPageJsonLd` schema
 *  (which needs plain-text answers) and the rendered FAQ accordion (which
 *  receives the parsed anchor segments to render the link as a real
 *  React element rather than as `dangerouslySetInnerHTML`). */
export interface OnDemandFaqEntry {
  title: string
  /** Full plain-text rendering with the anchor unwrapped to its label.
   *  Used by `FAQPageJsonLd` `acceptedAnswerText`. */
  descriptionPlain: string
  /** Anchor segments for in-component rendering. */
  parsed: ReturnType<typeof parseSingleAnchor>
  /** Resolved destination URL — also embedded in `parsed.href`. */
  href: string
}

/**
 * Builds the appended FAQ entry for the on-demand pillar landing surface.
 * Returns `null` when the surface should not render (per
 * `resolveOnDemandLandingUrl`).
 *
 * Callers supply already-resolved `title` + `description` strings from their
 * own typed `t()` (the project's i18next typed-keys rule prevents passing
 * the typed `TFunction` through a loose-signature wrapper — keeping
 * resolution at the call site preserves type safety without an unsafe cast).
 */
export function buildOnDemandFaqEntry(
  pillar: PillarPage,
  currentCity: string | undefined,
  strings: { title: string; description: string }
): OnDemandFaqEntry | null {
  const href = resolveOnDemandLandingUrl(pillar, currentCity)
  if (!href) return null

  const { title, description } = strings
  const parsed = parseSingleAnchor(description)
  // Plain text: replace the anchor with just its label. Schema.org
  // FAQPage `acceptedAnswerText` is plain-text, not HTML — the anchor
  // would render as the literal markup in Google rich results.
  const descriptionPlain = parsed.label
    ? `${parsed.before}${parsed.label}${parsed.after ?? ''}`
    : description

  return {
    title,
    descriptionPlain,
    parsed: { ...parsed, href },
    href
  }
}
